/**
 * Fullscreen 3D-viewer overlay (WHA-3710).
 *
 * The `.ifc` preview modal's "Open 3D model" used to navigate to the
 * `/buildings/:id/model` route. Instead we open the *same* viewer as a
 * fullscreen overlay in place, so closing it returns the user exactly where
 * they were (the documents list, an issue, a chat DocChip…) with no route or
 * URL change.
 *
 * The viewer (`IfcViewerPage` → `IfcViewer` → `ModelPicker`) is coupled to the
 * router: it reads the building id from `useParams` and the selected model from
 * the `?ifc=` query param via `useSearchParams`. We satisfy that — without
 * touching the browser URL — by mounting it inside an isolated `MemoryRouter`
 * seeded with `/buildings/<id>/model?ifc=<docId>`. That router is a sibling of
 * the app's `BrowserRouter` (this provider wraps both), so there's no nested-
 * router conflict, and every read/write the viewer does to "the URL" stays in
 * memory.
 *
 * Mounted once at the app root (inside the language/store/org/query providers),
 * so any surface that shows an `.ifc` preview can trigger it via
 * `useIfcModelOverlay().openModel(buildingId, docId)`.
 */

import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { MemoryRouter, Route, Routes } from "react-router-dom";
import { X } from "lucide-react";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import { lazyWithReload } from "@/utils/lazyWithReload";
import { IfcViewerModeProvider } from "./ifcViewerMode";

// Lazy so the three.js / @thatopen 3D stack stays in its own chunk and out of
// the entry bundle — this provider is imported eagerly by App, but the heavy
// viewer code only loads when the overlay actually opens. Mirrors App.tsx's
// route-level lazy import of the same page (WHA-3689). lazyWithReload recovers
// a stale tab whose old chunk hash 404s to the SPA fallback after a redeploy.
const IfcViewerPage = lazyWithReload(() =>
  import("./IfcViewerPage").then((m) => ({ default: m.IfcViewerPage }))
);

interface OverlayTarget {
  buildingId: number;
  docId: number;
}

interface IfcModelOverlayContextValue {
  /** Open the fullscreen 3D viewer for a building, preloading one IFC doc. */
  openModel: (buildingId: number, docId: number) => void;
}

const IfcModelOverlayContext = createContext<IfcModelOverlayContextValue | null>(null);

export function useIfcModelOverlay(): IfcModelOverlayContextValue {
  const ctx = useContext(IfcModelOverlayContext);
  if (!ctx) {
    throw new Error("useIfcModelOverlay must be used within an IfcModelOverlayProvider");
  }
  return ctx;
}

export function IfcModelOverlayProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<OverlayTarget | null>(null);

  const openModel = useCallback((buildingId: number, docId: number) => {
    setTarget({ buildingId, docId });
  }, []);
  const close = useCallback(() => setTarget(null), []);

  const value = useMemo(() => ({ openModel }), [openModel]);

  return (
    <IfcModelOverlayContext.Provider value={value}>
      {children}
      {target && (
        <IfcModelOverlay buildingId={target.buildingId} docId={target.docId} onClose={close} />
      )}
    </IfcModelOverlayContext.Provider>
  );
}

interface IfcModelOverlayProps {
  buildingId: number;
  docId: number;
  onClose: () => void;
}

function IfcModelOverlay({ buildingId, docId, onClose }: IfcModelOverlayProps) {
  const { t } = useLanguage();
  const tv = t.buildings.ifcViewer;

  // Escape closes the viewer, matching the "Close viewer" button.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // WHA-3729: mark <body> while the overlay (z-[60], opaque) is open so the
  // global rule in index.css lifts every Radix layer portaled to <body>
  // (issue pickers, menus, tooltips, hover cards, nested dialogs) above it —
  // otherwise they open behind the overlay and freeze the page.
  useEffect(() => {
    document.body.classList.add("ifc-model-overlay-open");
    return () => document.body.classList.remove("ifc-model-overlay-open");
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-slate-900">
      {/* MemoryRouter keeps the viewer's `useParams`/`?ifc=` handling working
          without ever touching the browser URL. Open to all org members; the
          previewed `.ifc` is one the user could already see in the document list
          (RLS-scoped), so no extra permission gate here. */}
      <IfcViewerModeProvider value={{ preview: true, previewDocId: docId }}>
        <MemoryRouter initialEntries={[`/buildings/${buildingId}/model?ifc=${docId}`]}>
          <Suspense
            fallback={
              <div className="flex h-full w-full items-center justify-center text-sm text-slate-400">
                {tv.loadingModels}
              </div>
            }
          >
            <Routes>
              <Route path="/buildings/:id/model" element={<IfcViewerPage />} />
            </Routes>
          </Suspense>
        </MemoryRouter>
      </IfcViewerModeProvider>

      {/* "Close viewer" pill, top-centre — clear of the top-left controls and
          the top-right model picker. */}
      <button
        type="button"
        onClick={onClose}
        className="absolute left-1/2 top-3 z-[70] flex h-11 -translate-x-1/2 items-center gap-2 rounded-xl border border-white/10 bg-slate-800/70 px-4 text-sm font-medium text-slate-200 shadow-sm backdrop-blur-md transition-colors hover:bg-slate-700/80 hover:text-white"
      >
        <X className="size-5" />
        {tv.closeViewer}
      </button>
    </div>,
    document.body
  );
}
